import * as React from "react";
import { useState } from "react";
import "./ConvertToPatientForm.scss";
import ModalPopup from "../commons/components/modal/ModalPopup";
import { FormSection } from "../commons/components/form/FormWithSections";
import TextInput from "../commons/components/form/input/TextInput";
import { required } from "../commons/model/form/Validators";
import DateInput from "../commons/components/form/input/DateInput";
import Form from "../commons/components/form/Form";
import EmailInput from "../commons/components/form/input/EmailInput";
import TelInput from "../commons/components/form/input/TelInput";
import Country from "../commons/model/address/Country";
import SelectInput from "../commons/components/form/input/SelectInput";
import {
  PatientDoctor,
  PatientInformation,
  PatientPractice,
} from "../commons/model/patient/PatientInfo";
import sfetch from "../commons/utils/super-fetch";
import Doctor from "../commons/model/Doctor";
import Practice from "../commons/model/Practice";
import GroupSelectInput from "../commons/components/form/input/GroupSelectInput";
import { ErrorCode } from "../commons/utils/errorCode";
import {
  buildDoctorGroupOptions,
  buildDoctorTopOptions,
  buildPracticeOptions,
} from "../patient-profile/AssignmentDropdownBuilder";
import { CustomErrors } from "../commons/model/form/Field";
import { createRoot } from "react-dom/client";

interface ConvertToPatientFormProps {
  countries: Country[];
  patientInformation: PatientInformation;
  practiceInfo?: PatientPractice;
  doctorInfo?: PatientDoctor;
  leadProfileLink: string;
  convertUrl: string;
  canEditDoctorAssignment: boolean;
  canEditPracticeAssignment: boolean;
  assignableDoctors: Doctor[];
  assignablePractices: Practice[];
  assignmentUrl: string;
}

interface ConvertToPatientFormState {
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  phoneNumber?: string;
  email?: string;
  practiceSoftwareId?: string;
  medicalFileNumber?: string;
  selectedPracticeId?: string;
  selectedDoctorId?: string;
}

interface ConvertToPatientFormSection {
  title: string;
  content?: React.JSX.Element;
}

export default function ConvertToPatientForm(props: ConvertToPatientFormProps) {
  const [state, setState] = useState<ConvertToPatientFormState>({
    firstName: props.patientInformation.firstName,
    lastName: props.patientInformation.lastName,
    birthDate: props.patientInformation.birthDate,
    phoneNumber: props.patientInformation.phoneNumbers.primaryPhoneNumber,
    email: props.patientInformation.email,
    selectedPracticeId: props.practiceInfo?.practiceUid,
    selectedDoctorId: props.doctorInfo?.doctorUid,
  });
  const [showError, setShowError] = useState(false);
  const [errorCauses, setErrorCauses] = useState<string[]>([]);
  const practiceOptions = buildPracticeOptions(
    props.assignablePractices,
    !props.practiceInfo?.practiceUid
  );
  const topDoctorOption = buildDoctorTopOptions(!props.doctorInfo?.doctorUid);
  const doctorGroupOptions = buildDoctorGroupOptions(props.assignableDoctors);
  const [firstnameInputCustomError, setFirstnameInputCustomError] =
    useState<CustomErrors>(undefined);
  const [lastnameInputCustomError, setLastnameInputCustomError] = useState<CustomErrors>(undefined);
  const [emailInputCustomError, setEmailInputCustomError] = useState<CustomErrors>(undefined);
  const [dateOfBirthInputCustomError, setDateOfBirthInputCustomError] =
    useState<CustomErrors>(undefined);
  const [phoneInputCustomError, setPhoneInputCustomError] = useState<CustomErrors>(undefined);
  const [medicalFileNumberInputCustomError, setMedicalFileNumberInputCustomError] =
    useState<CustomErrors>(undefined);
  const [externalIdInputCustomError, setExternalIdInputCustomError] =
    useState<CustomErrors>(undefined);

  const sections: ConvertToPatientFormSection[] = [
    {
      title: i18n["Form.PersonalInfo.Section.Personal"],
      content: (
        <div className="h-flex with-gap space-between align-center">
          <TextInput
            name="PatientFirstName"
            label={i18n["Global.FirstName"]}
            placeholder={i18n["Form.Field.Placeholder.FirstName"]}
            initialValue={state.firstName}
            onChange={(inputState) => {
              setFirstnameInputCustomError(undefined);
              setState({ ...state, firstName: inputState.value });
            }}
            validators={[required()]}
            customErrors={firstnameInputCustomError}
          />
          <TextInput
            name="PatientLastName"
            label={i18n["Global.LastName"]}
            placeholder={i18n["Form.Field.Placeholder.LastName"]}
            initialValue={state.lastName}
            onChange={(inputState) => {
              setLastnameInputCustomError(undefined);
              setState({ ...state, lastName: inputState.value });
            }}
            validators={[required()]}
            customErrors={lastnameInputCustomError}
          />
          <DateInput
            name="PatientBirthDate"
            label={i18n["Global.Birthdate"]}
            placeholder={i18n["Form.Field.Placeholder.Birthdate"]}
            initialValue={state.birthDate}
            onChange={(inputState) => {
              setDateOfBirthInputCustomError(undefined);
              setState({ ...state, birthDate: inputState.value });
            }}
            validators={[required()]}
            customErrors={dateOfBirthInputCustomError}
          />
        </div>
      ),
    },
    {
      title: i18n["Form.PersonalInfo.Section.Contact"],
      content: (
        <div className="h-flex with-gap space-between align-center">
          <EmailInput
            name="PatientEmail"
            label={i18n["Global.EmailAddress"]}
            placeholder={i18n["Form.Field.Placeholder.Email"]}
            initialValue={state.email}
            onChange={(inputState) => {
              setEmailInputCustomError(undefined);
              setState({ ...state, email: inputState.value });
            }}
            customErrors={emailInputCustomError}
          />
          <TelInput
            name="PatientTel"
            availableCountries={props.countries}
            label={i18n["Global.PhoneNumber"]}
            placeholder={i18n["Form.Field.Placeholder.CellPhoneNumber"]}
            initialValue={state.phoneNumber}
            onChange={(inputState) => {
              setPhoneInputCustomError(undefined);
              setState({ ...state, phoneNumber: inputState.value });
            }}
            customErrors={phoneInputCustomError}
          />
        </div>
      ),
    },
    {
      title: i18n["Page.Lead.ConvertToPatient.Form.PracticeInfo"],
      content: (
        <div className="h-flex with-gap space-between align-center">
          <SelectInput
            name="PracticeName"
            initialValue={state.selectedPracticeId || ""}
            options={practiceOptions}
            label={i18n["Page.Lead.ConvertToPatient.Form.Practice"]}
            onChange={(inputState) => setState({ ...state, selectedPracticeId: inputState.value })}
            validators={[required()]}
            disabled={!props.canEditPracticeAssignment}
          />
          <GroupSelectInput
            name="DoctorName"
            initialValue={state.selectedDoctorId || ""}
            topOptions={topDoctorOption}
            groupOptions={doctorGroupOptions}
            label={i18n["Page.Lead.ConvertToPatient.Form.Doctor"]}
            onChange={(inputState) => setState({ ...state, selectedDoctorId: inputState.value })}
            validators={[required()]}
            disabled={!props.canEditDoctorAssignment}
          />
        </div>
      ),
    },
    {
      title: i18n["Page.Lead.ConvertToPatient.Form.AdditionalInfo"],
      content: (
        <div className="v-flex with-gap">
          <div className="h-flex with-gap space-between align-center">
            <TextInput
              name="PracticeSoftwareIdentifier"
              label={i18n["Page.Lead.ConvertToPatient.Form.PracticeSoftwareIdentifier"]}
              onChange={(inputState) => {
                setExternalIdInputCustomError(undefined);
                setState({ ...state, practiceSoftwareId: inputState.value });
              }}
              customErrors={externalIdInputCustomError}
            />
            <TextInput
              name="MedicalFileNumber"
              label={i18n["Page.Lead.ConvertToPatient.Form.MedicalFileNumber"]}
              onChange={(inputState) => {
                setMedicalFileNumberInputCustomError(undefined);
                setState({ ...state, medicalFileNumber: inputState.value });
              }}
              customErrors={medicalFileNumberInputCustomError}
            />
          </div>
        </div>
      ),
    },
  ];

  const handleSubmit = async () => {
    if (
      state.selectedDoctorId != props.doctorInfo?.doctorUid ||
      state.selectedPracticeId != props.practiceInfo?.practiceUid
    ) {
      sfetch(props.assignmentUrl, {
        method: "PATCH",
        body: JSON.stringify({
          doctorId: { value: state.selectedDoctorId },
          practiceId: { value: state.selectedPracticeId },
        }),
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then(() => {
          setShowError(false);
          convertLead();
        })
        .catch((error) => {
          error.json().then((response) => {
            if (response.code == ErrorCode.DOCTOR_PRATICE_MISMATCH) {
              setErrorCauses([i18n["Lead.Assignment.DoctorPracticeMismatch"]]);
            } else {
              setErrorCauses([i18n["Global.TechnicalError"]]);
            }
            setShowError(true);
          });
        });
    } else {
      convertLead();
    }
  };

  const convertLead = () => {
    sfetch(props.convertUrl, {
      method: "PUT",
      body: JSON.stringify(state),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(() => {
        document.location.assign(props.leadProfileLink);
      })
      .catch((error) => {
        error.json().then((response) => {
          if (response.code == ErrorCode.LEAD_TO_PATIENT_CONVERSION_EXCEPTION) {
            setErrorCauses([response.message]);
            setShowError(true);
          } else if (response.code == ErrorCode.LEAD_TO_PATIENT_CONVERSION_EXCEPTION_WITH_FIELDS) {
            const causes = response.causes;
            setFirstnameInputCustomError(
              new CustomErrors(Boolean(causes.firstname), causes.firstname)
            );
            setLastnameInputCustomError(
              new CustomErrors(Boolean(causes.lastname), causes.lastname)
            );
            setEmailInputCustomError(new CustomErrors(Boolean(causes.email), causes.email));
            setPhoneInputCustomError(new CustomErrors(Boolean(causes.phone), causes.phone));
            setDateOfBirthInputCustomError(
              new CustomErrors(Boolean(causes.date_of_birth), causes.date_of_birth)
            );
            setExternalIdInputCustomError(
              new CustomErrors(Boolean(causes.external_id), causes.external_id)
            );
            setMedicalFileNumberInputCustomError(
              new CustomErrors(Boolean(causes.medical_file_number), causes.medical_file_number)
            );
          } else {
            setErrorCauses([i18n["Global.TechnicalError"]]);
            setShowError(true);
          }
        });
      });
  };

  return (
    <ModalPopup
      closeModal={() => document.location.assign(props.leadProfileLink)}
      fullscreen={true}
    >
      <div className="v-flex centered">
        <div className="modal-title">{i18n["Page.Lead.ConvertToPatient.Title"]}</div>
        <div className="modal-content">
          <div className="convert-to-patient-form v-flex">
            <p className="message info">{i18n["Page.Lead.ConvertToPatient.Message"]}</p>
            {showError && (
              <div className="conversion-error">
                <div className="message danger">
                  {i18n["Page.Lead.ConvertToPatient.Error.Message"]}
                </div>
                <ul>
                  {errorCauses.map((cause) => (
                    <li>{cause}</li>
                  ))}
                </ul>
              </div>
            )}
            <Form
              className="v-flex"
              submitLabel={i18n["Page.PatientManagement.Profile.Convert"]}
              submit={handleSubmit}
            >
              {sections.map((section, index) => (
                <div className="form-section-wrapper">
                  <FormSection number={index + 1} title={section.title}>
                    {section.content}
                  </FormSection>
                </div>
              ))}
            </Form>
          </div>
        </div>
      </div>
    </ModalPopup>
  );
}

ConvertToPatientForm.loadComponent = (elementId: string, props: ConvertToPatientFormProps) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<ConvertToPatientForm {...props} />);
};
